import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Text } from '@atlaskit/primitives/compiled/text';
import Spinner from '@atlaskit/spinner/spinner';
import { token } from '@atlaskit/tokens';

import { EMBEDDED_MENU_SOURCE } from './analytics.ts';
import type { RefreshLinkUrlHandler } from './connectionTypes.ts';
import { EmbeddedLinkSection } from './embedded-menu/EmbeddedLinkSection.tsx';
import { useMenuViewAnalytics } from './hooks/useMenuViewAnalytics.ts';
import {
	DEFAULT_VISIBLE_LINK_COUNT,
	getTopConnectionLinks,
} from './utils/getTopConnectionLinks.ts';
import { relatedContentHeadingMessage } from './utils/menuSectionHeading.ts';
import type { EnhancedSmartLink } from './utils/smartLinkTypes.ts';

const MENU_WIDTH = '320px';
const MENU_BODY_MAX_HEIGHT = '600px';

const i18n = defineMessages({
	menuLabel: {
		id: 'byline-connections.embedded-menu.label.do-not-translate',
		defaultMessage: 'Teamwork Graph',
		description:
			'Accessible name of the connections byline menu when Confluence is embedded in another product, where the menu has no visible title. "Teamwork Graph" is a product name and is intentionally not translated.',
	},
	linksTo: {
		id: 'byline-connections.embedded-menu.section.links-to',
		defaultMessage: 'Links to ({count})',
		description:
			'Heading of the links section of the connections byline menu when Confluence is embedded in another product, followed by the number of links found on the current page in parentheses.',
	},
	empty: {
		id: 'byline-connections.embedded-menu.empty',
		defaultMessage: 'This content doesn’t have any related content or links yet.',
		description:
			'Message shown in the connections byline menu when Confluence is embedded in another product and the current page has no related content and no links.',
	},
});

const styles = cssMap({
	menu: {
		width: MENU_WIDTH,
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.medium'),
	},
	content: {
		paddingBlock: token('space.100'),
		maxHeight: MENU_BODY_MAX_HEIGHT,
		overflowY: 'auto',
	},
	emptyState: {
		display: 'flex',
		paddingBlock: token('space.200'),
		paddingInline: token('space.200'),
		justifyContent: 'center',
	},
});

export type EmbeddedConnectionsByLineMenuProps = {
	links?: EnhancedSmartLink[];
	relatedContentLinks?: EnhancedSmartLink[];
	relatedContentLoading?: boolean;
	refreshLinkUrl: RefreshLinkUrlHandler;
	onNavigate?: () => void;
};

/**
 * The connections byline menu for embedded Confluence (e.g. a page inside Jira), gated by
 * `confluence_1p_and_3p_connection_byline_experiment` or `migrate_planner_to_docs_tab`. It lists
 * only Related content and Links to, each expanding inline, because embedded Confluence cannot
 * open the Teamwork Graph preview panel.
 */
export const EmbeddedConnectionsByLineMenu = ({
	links,
	relatedContentLinks,
	relatedContentLoading = false,
	refreshLinkUrl,
	onNavigate,
}: EmbeddedConnectionsByLineMenuProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const relatedLinks = useMemo(() => relatedContentLinks ?? [], [relatedContentLinks]);
	// Same order as the preview panel's Links to: unconnected first, then document order.
	const linksToLinks = useMemo(
		() => getTopConnectionLinks(links ?? [], links?.length ?? 0),
		[links],
	);
	const initiallyVisibleLinks = useMemo(
		() => [
			...relatedLinks.slice(0, DEFAULT_VISIBLE_LINK_COUNT),
			...linksToLinks.slice(0, DEFAULT_VISIBLE_LINK_COUNT),
		],
		[relatedLinks, linksToLinks],
	);
	// Embedded views report immediately; loading only controls the empty-state spinner.
	useMenuViewAnalytics({
		visibleLinks: initiallyVisibleLinks,
		ready: true,
		source: EMBEDDED_MENU_SOURCE,
	});
	const isEmpty = relatedLinks.length === 0 && linksToLinks.length === 0;

	return (
		<Box
			xcss={styles.menu}
			role="dialog"
			aria-label={formatMessage(i18n.menuLabel)}
			testId="connections-byline-embedded-menu"
		>
			<Box xcss={styles.content} testId="connections-byline-embedded-menu.content">
				<EmbeddedLinkSection
					section="relatedContent"
					// The embedded menu expands inline via Show more, so its heading always counts every row.
					heading={formatMessage(relatedContentHeadingMessage, {
						count: relatedLinks.length,
						isTruncated: 'false',
					})}
					links={relatedLinks}
					linkDirection="incoming"
					refreshLinkUrl={refreshLinkUrl}
					onNavigate={onNavigate}
				/>
				<EmbeddedLinkSection
					section="linksTo"
					heading={formatMessage(i18n.linksTo, { count: linksToLinks.length })}
					links={linksToLinks}
					linkDirection="outgoing"
					refreshLinkUrl={refreshLinkUrl}
					onNavigate={onNavigate}
					compact
				/>
				{isEmpty && relatedContentLoading ? (
					<Box xcss={styles.emptyState}>
						<Spinner size="medium" testId="connections-byline-embedded-menu.loading" />
					</Box>
				) : null}
				{isEmpty && !relatedContentLoading ? (
					<Box xcss={styles.emptyState} testId="connections-byline-embedded-menu.empty">
						<Text color="color.text.subtlest">{formatMessage(i18n.empty)}</Text>
					</Box>
				) : null}
			</Box>
		</Box>
	);
};
